import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import api from '@/api/axios';
import {
  THEMES, DEFAULT_THEME, isValidTheme, readStoredTheme, storeTheme,
} from '@/constants/themes';

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  setTheme: () => {},
  syncFromServer: async () => {},
  themes: THEMES,
});

export const useTheme = () => useContext(ThemeContext);

/** html[data-theme] 에 반영한다. index.html 의 초기 스크립트와 같은 동작이다. */
const applyTheme = (value) => {
  const root = document.documentElement;
  if (root.getAttribute('data-theme') === value) return;

  // 전환하는 동안만 색 트랜지션을 켠다(항상 켜 두면 첫 렌더가 느려 보인다).
  root.classList.add('theme-switching');
  root.setAttribute('data-theme', value);
  window.setTimeout(() => root.classList.remove('theme-switching'), 260);
};

/**
 * 테마 제공자.
 *
 * 저장 위치가 두 곳인 이유:
 *  1) localStorage — 새로고침해도 깜빡임 없이 즉시 적용된다(index.html 초기 스크립트가 읽는다).
 *  2) 서버(계정) — 다른 기기에서 로그인해도 같은 테마로 열린다.
 * 서버 저장이 실패해도 화면은 이미 바뀐 뒤이므로 조용히 넘어간다.
 */
export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readStoredTheme);

  useEffect(() => { applyTheme(theme); }, [theme]);

  /**
   * 계정에 저장된 테마를 가져와 반영한다.
   * 로그인 전에는 401 이 나므로 인증이 끝난 뒤(MainLayout)에서 한 번 호출한다.
   */
  const syncFromServer = useCallback(async () => {
    try {
      const res = await api.get('/mypage/ui-settings');
      const serverTheme = res.data?.theme;
      if (!isValidTheme(serverTheme) || serverTheme === readStoredTheme()) return;

      setThemeState(serverTheme);
      storeTheme(serverTheme);
    } catch {
      // 서버가 응답하지 않으면 이 기기에 저장된 값을 그대로 쓴다.
    }
  }, []);

  const setTheme = useCallback((value) => {
    if (!isValidTheme(value)) return;

    setThemeState(value);
    storeTheme(value);

    api.put('/mypage/ui-settings', { theme: value }).catch(() => {
      // 화면에는 이미 적용됐다. 이 기기에서는 localStorage 로 유지된다.
    });
  }, []);

  const value = useMemo(
    () => ({ theme, setTheme, syncFromServer, themes: THEMES }),
    [theme, setTheme, syncFromServer]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeContext;
